import { DataTable, EmptyState, StatusPill, type ColumnDef, type StatusTone } from '@vnetwork/ui/data';
import { Server } from 'lucide-react';
import { UsageMeter } from './UsageMeter';
import type { ZoneHost } from '@/api/opennebula/zones';

/** Physical hosts in the zone — state, cluster, VM count, CPU + memory usage. */
interface Props {
  hosts: ZoneHost[];
}

function stateTone(state?: string): StatusTone {
  const s = String(state ?? '').toUpperCase();
  if (s === 'MONITORED' || s === 'MONITORING_MONITORED') return 'success';
  if (s === 'ERROR' || s === 'MONITORING_ERROR') return 'danger';
  if (s === 'DISABLED' || s === 'OFFLINE' || s === 'MONITORING_DISABLED') return 'muted';
  return 'warning';
}

const columns: ColumnDef<ZoneHost, unknown>[] = [
  { id: 'name', header: 'Name', cell: ({ row }) => <span style={{ fontWeight: 500 }}>{row.original.name}</span> },
  {
    id: 'state',
    header: 'State',
    cell: ({ row }) => <StatusPill tone={stateTone(row.original.state)}>{row.original.state || 'UNKNOWN'}</StatusPill>,
  },
  { id: 'cluster', header: 'Cluster', cell: ({ row }) => row.original.cluster_name || '—' },
  { id: 'vms', header: 'VMs', cell: ({ row }) => row.original.vm_count ?? 0 },
  {
    id: 'cpu',
    header: 'Allocated CPU',
    cell: ({ row }) => <UsageMeter used={Number(row.original.cpu_used ?? 0)} total={Number(row.original.cpu_total ?? 0)} />,
  },
  {
    id: 'memory',
    header: 'Allocated Memory',
    cell: ({ row }) => (
      // Backend returns MB; show GB for readability.
      <UsageMeter
        used={Math.round(Number(row.original.mem_used ?? 0) / 1024)}
        total={Math.round(Number(row.original.mem_total ?? 0) / 1024)}
        suffix=" GB"
      />
    ),
  },
];

export function HostsTable({ hosts }: Props) {
  return (
    <DataTable
      columns={columns}
      data={hosts}
      getRowId={(h) => String(h.id)}
      emptyState={<EmptyState icon={<Server size={28} />} title="No hosts" description="This zone has no physical hosts." />}
    />
  );
}
